import { ChangeEvent, KeyboardEvent, useEffect, useState } from "react";
import { Box, Button, ButtonBase, Skeleton } from "@mui/material";
import strings from "global/constants/StringConstants";
import urls from "global/constants/UrlConstants";
import { fillCopy } from "helpers/methods";
import { PromoCampaign, promoPercentFor } from "helpers/promotionHelpers";
import upgradePlanTokens from "screens/Settings/MyPlan/UpgradePlan.tokens";
import {
  backLinkSx,
  compareLinkSx,
  cycleItemSx,
  cycleTrackSx,
  panelSubSx,
  panelSx,
  panelTextColumnSx,
  panelTitleSx,
  planCardSx,
  planGridSx,
  promoPillSx,
  saveBadgeSx,
  seatStepperButtonSx,
  seatStepperInputSx,
  seatStepperSx,
  seatStepperValueSx,
  seatUpdateButtonSx,
  upgradeViewSx,
} from "screens/Settings/MyPlan/UpgradePlan.styles";
import {
  SeatUpdateBanner,
  UpgradePlanCard,
} from "screens/Settings/MyPlan/components/UpgradePlanHelper";
import { PlanPromoStrip } from "screens/Settings/MyPlan/components/PlanPromo";
import PlanCard from "./PlanCard";
import { BackArrowIcon, ExternalLinkIcon } from "./UpgradePlanIcons";

const t = upgradePlanTokens;

/**
 * "Choose your plan" — seat counter, billing-cycle switch, optional seat-only
 * update banner and the four plan columns. Purely presentational: every derived
 * value arrives as a prop from `UpgardePlan`.
 */
const OnboardingStep1 = (props: {
  isLoading: boolean;
  seats: number;
  seatHint: string;
  onSeatChange: (seats: number) => void;
  isAnnual: boolean;
  onCycleChange: (isAnnual: boolean) => void;
  annualSavingsPercent: number;
  cards: UpgradePlanCard[];
  seatUpdate: SeatUpdateBanner | null;
  onSeatUpdate: () => void;
  onSelectPlan: (plan: any) => void;
  onBack: () => void;
  /** A seat check is in flight — hold off further plan selections. */
  isBusy?: boolean;
  /** Plan whose seat check is in flight; its CTA shows a spinner. */
  checkingPlanId?: string;
  /** Renders the counter read-only (no stepper buttons). */
  isSeatCountFixed?: boolean;
  /** Heading above the seat count. */
  seatTitle?: string;
  /** Read the plan catalog again after it came back empty. */
  onRetry?: () => void;
  /** Seasonal campaign running for this viewer — strip and cycle pills. */
  promoCampaign?: PromoCampaign | null;
}) => {
  const promoCycleLabel = (annual: boolean) =>
    props.promoCampaign
      ? fillCopy(
          annual
            ? strings.promotions.plan.cycleAnnual
            : strings.promotions.plan.cycleMonthly,
          { pct: promoPercentFor(props.promoCampaign, annual) },
        )
      : "";

  // The counter itself only floors at 1 — picking fewer seats than the
  // account's active users is still allowed here; the caller (`UpgardePlan`)
  // re-clamps to that floor before it's ever quoted or bought, and runs the
  // seat-reduction check against the clamped count. This screen is a rough
  // estimate, not the enforcement point.
  const seatFloor = t.seats.min;

  /**
   * The typed seat count is held as text while editing so the field can be
   * cleared mid-edit (typing "12" over "3" means passing through an empty
   * value). The bounds are applied when the edit finishes, not per keystroke,
   * which would fight anyone typing a number that starts below the floor.
   */
  const [draft, setDraft] = useState<string>(String(props.seats));

  useEffect(() => {
    setDraft(String(props.seats));
  }, [props.seats]);

  const handleSeatInput = (event: ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 4);
    setDraft(digits);
    const parsed = Number(digits);
    // Report the upper-bounded value as it's typed so the plan cards, the hint
    // and the totals keep up; the floor waits for blur.
    if (digits) props.onSeatChange(Math.min(t.seats.max, Math.max(1, parsed)));
  };

  const commitSeatInput = () => {
    const parsed = Number(draft);
    const seats = Number.isFinite(parsed) && parsed > 0 ? parsed : seatFloor;
    const clamped = Math.min(t.seats.max, Math.max(seatFloor, seats));
    setDraft(String(clamped));
    if (clamped !== props.seats) props.onSeatChange(clamped);
  };

  /**
   * The catalog read finished and produced nothing to sell. Shimmering
   * columns would claim plans are still on the way, so this is reported as
   * the disruption it is, with the read offered again.
   */
  const plansUnavailable = !props.isLoading && props.cards.length === 0;

  const getPlanColumns = () => {
    if (props.isLoading) {
      return [0, 1, 2, 3].map((item) => (
        <Box key={item} sx={planCardSx(false, false)}>
          <Skeleton variant="text" width="55%" height={22} />
          <Skeleton variant="text" width="45%" height={34} />
          <Skeleton variant="rectangular" height={92} />
          <Skeleton
            variant="rectangular"
            height={Number.parseInt(t.size.planCtaHeight, 10)}
          />
        </Box>
      ));
    }
    return props.cards.map((card) => (
      <PlanCard
        key={card.id}
        card={card}
        isBusy={props.isBusy}
        isChecking={Boolean(card.id) && card.id === props.checkingPlanId}
        promoCampaign={props.promoCampaign}
        onSelect={props.onSelectPlan}
      />
    ));
  };

  return (
    <Box sx={upgradeViewSx}>
      <ButtonBase
        id="upgrade-plan-back-btn"
        disableRipple
        sx={backLinkSx}
        onClick={props.onBack}
      >
        <BackArrowIcon />
        Back to plan summary
      </ButtonBase>

      {props.promoCampaign && <PlanPromoStrip campaign={props.promoCampaign} />}

      {/* Seat counter */}
      <Box sx={panelSx(false)}>
        <Box sx={panelTextColumnSx}>
          <Box component="span" sx={panelTitleSx}>
            {props.seatTitle ?? "How many people will use Inkfree?"}
          </Box>
          <Box component="span" sx={panelSubSx(false)}>
            {props.seatHint}
          </Box>
        </Box>
        <Box
          sx={seatStepperSx}
          role="group"
          aria-label={props.seatTitle ?? "How many people will use Inkfree?"}
        >
          {!props.isSeatCountFixed && (
            <ButtonBase
              id="upgrade-plan-seat-decrement"
              aria-label="Remove a seat"
              disableRipple
              disabled={props.seats <= seatFloor}
              sx={seatStepperButtonSx}
              onClick={() =>
                props.onSeatChange(Math.max(seatFloor, props.seats - 1))
              }
            >
              −
            </ButtonBase>
          )}
          {props.isSeatCountFixed ? (
            <Box sx={seatStepperValueSx} aria-live="polite" aria-atomic="true">
              {props.seats}
            </Box>
          ) : (
            <Box
              component="input"
              id="upgrade-plan-seat-input"
              type="text"
              inputMode="numeric"
              aria-label="Number of seats"
              sx={seatStepperInputSx}
              value={draft}
              onChange={handleSeatInput}
              onBlur={commitSeatInput}
              onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                if (event.key === "Enter") event.currentTarget.blur();
              }}
            />
          )}
          {!props.isSeatCountFixed && (
            <ButtonBase
              id="upgrade-plan-seat-increment"
              aria-label="Add a seat"
              disableRipple
              disabled={props.seats >= t.seats.max}
              sx={seatStepperButtonSx}
              onClick={() =>
                props.onSeatChange(Math.min(t.seats.max, props.seats + 1))
              }
            >
              +
            </ButtonBase>
          )}
        </Box>
      </Box>

      {/* Billing cycle */}
      <Box sx={cycleTrackSx} role="group" aria-label="Billing cycle">
        <ButtonBase
          id="upgrade-plan-cycle-monthly"
          disableRipple
          aria-pressed={!props.isAnnual}
          sx={cycleItemSx(!props.isAnnual, Boolean(props.promoCampaign))}
          onClick={() => props.onCycleChange(false)}
        >
          Monthly
          {props.promoCampaign && (
            <Box
              component="span"
              sx={promoPillSx(props.promoCampaign, props.isAnnual, true)}
            >
              {promoCycleLabel(false)}
            </Box>
          )}
        </ButtonBase>
        <ButtonBase
          id="upgrade-plan-cycle-annual"
          disableRipple
          aria-pressed={props.isAnnual}
          sx={cycleItemSx(props.isAnnual, Boolean(props.promoCampaign))}
          onClick={() => props.onCycleChange(true)}
        >
          Annual
          {/* During a campaign its first-year offer replaces the everyday
              annual saving. */}
          {props.promoCampaign ? (
            <Box
              component="span"
              sx={promoPillSx(props.promoCampaign, !props.isAnnual, true)}
            >
              {promoCycleLabel(true)}
            </Box>
          ) : (
            <Box component="span" sx={saveBadgeSx}>
              {`SAVE ${props.annualSavingsPercent}%`}
            </Box>
          )}
        </ButtonBase>
      </Box>

      {/* Seat-only change on the current plan */}
      {props.seatUpdate && (
        <Box sx={panelSx(true)}>
          <Box sx={panelTextColumnSx}>
            <Box component="span" sx={panelTitleSx}>
              {props.seatUpdate.title}
            </Box>
            <Box component="span" sx={panelSubSx(true)}>
              {props.seatUpdate.description}
            </Box>
          </Box>
          <Button
            id="upgrade-plan-seat-update-btn"
            disableElevation
            sx={seatUpdateButtonSx}
            onClick={props.onSeatUpdate}
          >
            {props.seatUpdate.cta}
          </Button>
        </Box>
      )}

      {plansUnavailable ? (
        <Box sx={panelSx(false)} role="status">
          <Box sx={panelTextColumnSx}>
            <Box component="span" sx={panelTitleSx}>
              {strings.upgradePlan.loadFailedTitle}
            </Box>
            <Box component="span" sx={panelSubSx(false)}>
              {strings.upgradePlan.loadFailedBody}
            </Box>
          </Box>
          {props.onRetry && (
            <Button
              id="upgrade-plan-retry-btn"
              variant="outlined"
              onClick={props.onRetry}
            >
              {strings.upgradePlan.retryLabel}
            </Button>
          )}
        </Box>
      ) : (
        <Box sx={planGridSx}>{getPlanColumns()}</Box>
      )}

      <Box
        component="a"
        href={urls.pricingUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Compare all plans in detail (opens in a new tab)"
        sx={compareLinkSx}
      >
        Compare all plans in detail
        <ExternalLinkIcon />
      </Box>
    </Box>
  );
};

export default OnboardingStep1;
